/* eslint-disable */

/**
 * Variant picker — selection UX for PDP and quick-add.
 *
 * Interaction model (INP):
 *   1. Sync: resolve selection → commit id/URL → paint ATC + option label
 *   2. After paint: notify listeners (gallery, rewards, inventory)
 *   3. After another yield: price, availability, sku, etc.
 *
 * Strings: addToCart, noStock, noVariant, noSelectedVariant, onlyXLeft
 */

if (!customElements.get('variant-picker')) {
  class VariantPicker extends HTMLElement {
    constructor() {
      super();
      this.section = this.closest('.js-product');
      this.data = this.readProductData();
      this.deferToken = 0;

      this.sync();
      this.buildAvailabilityIndex();
      this.autoSelectSingleOptions();
      this.addEventListener('change', this.onChange.bind(this));

      // Quick-add drawer may still be mounting
      setTimeout(() => {
        this.updateAvailability();
        this.paintAddToCart();
      });
    }

    // ─── Change handling ─────────────────────────────────────────────

    onChange(evt) {
      this.sync();
      this.autoSelectSingleOptions();
      this.sync();

      this.commitAndPaint(evt);
      this.paintOptionLabel(evt);
      this.scheduleDeferred();
    }

    /**
     * Commit selected variant to forms/URL, then paint ATC.
     * Id input `change` is deferred (see scheduleDeferred) so Shop Pay /
     * instalments stay off the critical turn.
     */
    commitAndPaint(evt) {
      if (this.variant) {
        this.writeVariantId();
        this.writeUrl(evt);
      }
      this.paintAddToCart();
    }

    scheduleDeferred() {
      const token = ++this.deferToken;

      (async () => {
        await theme.yieldToMain();
        if (token !== this.deferToken) return;

        this.dispatchVariantIdChange();
        this.notifyChange();

        await theme.yieldToMain();
        if (token !== this.deferToken) return;

        if (this.variant) this.updatePrice();
        this.updateAvailability();
        this.updateWeight();
        this.updateBarcode();
        this.updateBackorderText();
        this.updatePickupAvailability();
        this.updateSku();
        this.updateMetafieldVisibility();
      })().catch((err) => console.error('[variant-picker]', err));
    }

    /** Gallery, rewards, inventory, quick-add media. */
    notifyChange() {
      if (this.selectedOptions.includes(null)) return;

      this.dispatchEvent(new CustomEvent('on:variant:change', {
        bubbles: true,
        detail: {
          form: this.form(),
          variant: this.variant,
          product: this.data.product
        }
      }));

      if (
        this.variant
        && this.variant.featured_media
        && this.section.matches('quick-add-drawer')
      ) {
        this.section.updateMedia(this.variant.featured_media.id);
      }
    }

    // ─── Selection state ─────────────────────────────────────────────

    sync() {
      this.optionSelectors = this.querySelectorAll('.option-selector');
      this.selectedOptions = this.readSelectedOptions();
      this.variant = this.matchVariant(this.selectedOptions);
      // Incomplete selection (any option still unset)
      this.preSelection = !this.variant && this.selectedOptions.includes(null);
    }

    readSelectedOptions() {
      return Array.from(this.optionSelectors, (selector) => {
        if (selector.dataset.selectorType === 'dropdown') {
          const btn = selector.querySelector('.custom-select__btn');
          const text = btn ? btn.textContent.trim() : this.dataset.placeholderText;
          return text === this.dataset.placeholderText ? null : text;
        }
        const input = selector.querySelector('input:checked');
        return input ? input.value : null;
      });
    }

    matchVariant(options) {
      if (options.includes(null)) return null;
      return this.data.product.variants.find((v) => (
        v.options.every((value, i) => value === options[i])
      ));
    }

    readProductData() {
      return JSON.parse(this.querySelector('[type="application/json"]').textContent);
    }

    optionValue(el, selector) {
      return selector.dataset.selectorType === 'dropdown' ? el.dataset.value : el.value;
    }

    // ─── Auto-select single-value options ────────────────────────────

    autoSelectSingleOptions() {
      const { options, variants } = this.data.product;
      if (!variants.length || !this.preSelection) return;

      const hasLockedOption = options.some((_, i) => {
        const first = variants[0].options[i];
        return variants.every((v) => v.options[i] === first);
      });
      if (!hasLockedOption) return;

      const available = variants.find((v) => v.available);
      if (available) this.applyVariantOptions(available);
    }

    applyVariantOptions(variant) {
      this.optionSelectors.forEach((selector, index) => {
        const value = variant.options[index];
        if (selector.dataset.selectorType === 'dropdown') {
          const customSelect = selector.querySelector('custom-select');
          if (customSelect && customSelect.selectOption) customSelect.selectOption(value);
          return;
        }
        const escaped = typeof CSS !== 'undefined' && CSS.escape
          ? CSS.escape(value)
          : value.replace(/\\/g, '\\\\').replace(/"/g, '\\"');
        const input = selector.querySelector(`input[value="${escaped}"]`);
        if (!input) return;
        input.checked = true;
        input.dispatchEvent(new Event('change', { bubbles: true }));
      });
    }

    // ─── ATC ─────────────────────────────────────────────────────────

    form() {
      return this.section && this.section.querySelector('.js-product-form-main');
    }

    paintAddToCart() {
      const form = this.form();
      if (!form) return;

      const btn = form.querySelector('[name="add"]');
      if (!btn) return;

      const available = Boolean(this.variant && this.variant.available);
      btn.disabled = this.preSelection || !available;
      btn.textContent = this.preSelection
        ? theme.strings.noSelectedVariant
        : available
          ? btn.dataset.addToCartText
          : (this.variant ? theme.strings.noStock : theme.strings.noVariant);
    }

    writeVariantId() {
      if (!this.section || !this.variant) return;
      this.section.querySelectorAll('.js-product-form-main, .js-instalments-form').forEach((form) => {
        const input = form.querySelector('input[name="id"]');
        if (!input) return;
        input.value = this.variant.id;
      });
    }

    /**
     * Notify payment buttons / instalments after ATC has painted (INP).
     * Value is already written sync in writeVariantId.
     */
    dispatchVariantIdChange() {
      if (!this.section || !this.variant) return;
      this.section.querySelectorAll('.js-product-form-main, .js-instalments-form').forEach((form) => {
        const input = form.querySelector('input[name="id"]');
        if (!input) return;
        input.dispatchEvent(new Event('change', { bubbles: true }));
      });
    }

    writeUrl(evt) {
      if (!evt || evt.type !== 'change' || this.dataset.updateUrl === 'false') return;
      window.history.replaceState({}, '', `${this.dataset.url}?variant=${this.variant.id}`);
    }

    paintOptionLabel(evt) {
      const input = evt && evt.target;
      if (!input || !input.closest) return;

      const selector = input.closest('.option-selector');
      if (!selector || selector.dataset.selectorType === 'dropdown') return;

      const label = selector.querySelector('.js-color-text');
      const value = input.nextElementSibling && input.nextElementSibling.querySelector('.js-value');
      if (label && value) label.textContent = value.textContent;
    }

    // ─── Availability ────────────────────────────────────────────────

    buildAvailabilityIndex() {
      const { variants } = this.data.product;
      this.availabilityIndex = Array.from(this.optionSelectors, (selector, selectorIndex) => {
        const byValue = new Map();

        selector.querySelectorAll('.js-option:not([data-value=""])').forEach((option) => {
          byValue.set(this.optionValue(option, selector), []);
        });

        variants.forEach((variant, variantIndex) => {
          const list = byValue.get(variant.options[selectorIndex]);
          if (list) list.push(variantIndex);
        });

        return byValue;
      });
    }

    static flagOption(optionEl, exists, available) {
      const text = exists ? theme.strings.noStock : theme.strings.noVariant;
      optionEl.classList.toggle('is-unavailable', !available);
      optionEl.classList.toggle('is-nonexistent', !exists);

      if (optionEl.classList.contains('custom-select__option')) {
        let em = optionEl.querySelector('em');
        if (em) em.hidden = available;
        if (!available) {
          if (em) {
            em.textContent = text;
          } else {
            optionEl.insertAdjacentHTML('beforeend', ` <em class="pointer-events-none">${text}</em>`);
          }
        }
        return;
      }

      const label = optionEl.nextElementSibling;
      if (!label) return;
      if (available) label.removeAttribute('title');
      else label.title = text;
    }

    updateAvailability() {
      if (this.dataset.showAvailability === 'false') return;

      const current = this.variant || { options: this.selectedOptions };

      if (this.dataset.availabilityMethod === 'selection') {
        this.updateAvailabilityBySelection(current);
      } else {
        this.updateAvailabilityDownward();
      }
    }

    updateAvailabilityBySelection(current) {
      this.querySelectorAll('.js-option').forEach((el) => {
        VariantPicker.flagOption(el, false, false);
      });

      this.optionSelectors.forEach((selector, selectorIndex) => {
        this.data.product.variants.forEach((variant) => {
          const othersMatch = variant.options.every((option, i) => (
            i === selectorIndex || option === current.options[i]
          ));
          if (!othersMatch) return;

          const el = Array.from(selector.querySelectorAll('.js-option')).find((opt) => (
            this.optionValue(opt, selector) === variant.options[selectorIndex]
          ));
          if (el) VariantPicker.flagOption(el, true, variant.available);
        });
      });
    }

    updateAvailabilityDownward() {
      this.optionSelectors.forEach((selector, selectorIndex) => {
        const index = this.availabilityIndex[selectorIndex] || new Map();

        selector.querySelectorAll('.js-option:not([data-value=""])').forEach((option) => {
          let exists = false;
          let available = false;

          (index.get(this.optionValue(option, selector)) || []).forEach((variantIndex) => {
            const variant = this.data.product.variants[variantIndex];
            let ok = true;
            for (let i = 0; i < selectorIndex; i += 1) {
              if (
                this.selectedOptions[i] !== null
                && variant.options[i] !== this.selectedOptions[i]
              ) {
                ok = false;
                break;
              }
            }
            if (!ok) return;
            exists = true;
            if (variant.available) available = true;
          });

          VariantPicker.flagOption(option, exists, available);
        });
      });
    }

    // ─── Secondary UI ────────────────────────────────────────────────

    updateMetafieldVisibility() {
      const selectedId = this.variant ? String(this.variant.id) : null;
      const ids = new Set(this.data.product.variants.map((v) => String(v.id)));

      document.querySelectorAll('[data-variant-metafield]').forEach((el) => {
        if (!ids.has(el.dataset.variantMetafield)) return;
        el.classList.toggle('hidden', el.dataset.variantMetafield !== selectedId);
      });
    }

    updateBackorderText() {
      this.backorder = this.backorder || this.section.querySelector('.backorder');
      if (!this.backorder) return;

      let hide = true;
      if (this.variant && this.variant.available) {
        const formatted = this.data.formatted[this.variant.id];
        const inventory = formatted && formatted.inventory;
        if (this.variant.inventory_management && inventory === 'none') {
          const title = this.section.querySelector('.product-title');
          const target = this.backorder.querySelector('.backorder__product');
          if (title && target) {
            const suffix = this.variant.title.includes('Default') ? '' : ` - ${this.variant.title}`;
            target.textContent = `${title.textContent}${suffix}`;
            hide = false;
          }
        }
      }
      this.backorder.hidden = hide;
    }

    updatePickupAvailability() {
      this.pickUpAvailability =
        this.pickUpAvailability || this.section.querySelector('pickup-availability');
      if (!this.pickUpAvailability) return;

      if (this.variant && this.variant.available) {
        this.pickUpAvailability.getAvailability(this.variant.id);
      } else {
        this.pickUpAvailability.removeAttribute('available');
        this.pickUpAvailability.innerHTML = '';
      }
    }

    updatePrice() {
      this.price = this.price || this.section.querySelector('.product-info__price > .price');
      if (!this.price) return;

      let { variant } = this;
      if (this.preSelection && this.data.product.variants.length) {
        variant = this.data.product.variants.reduce((lowest, v) => (
          v.price < lowest.price ? v : lowest
        ), this.data.product.variants[0]);
      }

      if (this.variant) {
        const current = this.price.querySelector('.price__current');
        const was = this.price.querySelector('.price__was');
        const unit = this.price.querySelector('.unit-price');
        const onSale = variant.compare_at_price > variant.price;
        const formatted = this.data.formatted[variant.id];

        current.querySelector('.js-label').textContent = onSale
          ? theme.strings.salePrice
          : theme.strings.regularPrice;
        current.querySelector('.js-value').innerHTML = formatted.price;

        if (was) {
          was.querySelector('.js-label').textContent = onSale ? theme.strings.regularPrice : '';
          was.querySelector('.js-value').innerHTML = onSale ? (formatted.compareAtPrice || '') : '';
        }

        if (this.variant.unit_price_measurement) {
          const ref = this.variant.unit_price_measurement.reference_value;
          const refUnit = this.variant.unit_price_measurement.reference_unit;
          this.price.querySelector('.unit-price__price').innerHTML = formatted.unitPrice;
          this.price.querySelector('.unit-price__unit').textContent =
            ref === 1 ? refUnit : `${ref} ${refUnit}`;
        }

        unit.hidden = !this.variant.unit_price_measurement;
        this.price.classList.toggle('price--on-sale', onSale);
        this.price.classList.toggle('price--sold-out', !this.variant.available && !this.preSelection);
      }

      this.price.querySelector('.price__default').hidden = !this.variant && !this.preSelection;
      this.price.querySelector('.price__no-variant').hidden = this.variant || this.preSelection;
      const from = this.price.querySelector('.price__from');
      if (from) from.hidden = !this.preSelection;
    }

    updateWeight() {
      this.weights = this.weights || this.section.querySelectorAll('.product-info__weight');
      if (!this.weights.length) return;
      const show = this.variant && this.variant.weight > 0;
      this.weights.forEach((el) => {
        el.textContent = show ? this.data.formatted[this.variant.id].weight : '';
        el.hidden = !show;
      });
    }

    updateBarcode() {
      this.barcodes = this.barcodes || this.section.querySelectorAll('.product-info__barcode-value');
      if (!this.barcodes.length) return;
      const show = Boolean(this.variant && this.variant.barcode);
      this.barcodes.forEach((el) => {
        el.textContent = show ? this.variant.barcode : '';
        el.parentNode.hidden = !show;
      });
    }

    updateSku() {
      this.sku = this.sku || this.section.querySelector('.product-sku__value');
      if (!this.sku) return;
      const show = Boolean(this.variant && this.variant.sku);
      this.sku.textContent = show ? this.variant.sku : '';
      this.sku.parentNode.hidden = !show;
    }
  }

  customElements.define('variant-picker', VariantPicker);
}
